import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useState } from 'react';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedState } from '@/std/hooks/useDebouncedState';
import { registrosSchema } from '@/std/schema';
import { apiProdutoMarcaGetQueryOptions } from '../api/apiProdutoMarcaGet';
import { ProdutoMarcaSelectSchema } from '../types/ProdutoMarca';

const schema = registrosSchema(ProdutoMarcaSelectSchema);
const fields = 'prm_idpk,prm_descricao';

export function ProdutoMarcaSelect({
    id,
    initialLabel,
    value,
    onChange,
}: {
    id?: string;
    initialLabel?: string | null;
    value?: number | null;
    onChange?: (value: number | null) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useDebouncedState('', DEBOUNCE_TIME);

    const { data, isFetching, error } = useQuery({
        ...apiProdutoMarcaGetQueryOptions({
            params: {
                fields,
                orderby: 'prm_descricao',
                pesquisar: search,
                qtde_registros: 10,
                registro_inicial: 0,
            },
            schema,
        }),
        enabled: open,
        staleTime: 0,
    });

    const { data: selectedData } = useQuery({
        ...apiProdutoMarcaGetQueryOptions({
            idpk: value ?? undefined,
            params: { fields },
            schema,
        }),
        enabled: typeof value === 'number' && initialLabel == null,
    });

    const options = (data?.registros ?? []).map((marca) => ({
        label: marca.prm_descricao,
        value: marca.prm_idpk,
    }));

    const selected = selectedData?.registros.find((marca) => marca.prm_idpk === value);
    const selectedLabel = initialLabel ?? selected?.prm_descricao;

    if (
        typeof value === 'number' &&
        selectedLabel != null &&
        !options.some((option) => option.value === value)
    ) {
        options.push({ label: selectedLabel, value });
    }

    return (
        <Select
            allowClear={true}
            className='w-full'
            id={id}
            loading={isFetching}
            notFoundContent={
                isFetching ? 'Pesquisando...' : error ? 'Erro ao consultar marcas' : undefined
            }
            onChange={(newValue: number | undefined) => onChange?.(newValue ?? null)}
            onClear={() => setSearch('')}
            onOpenChange={setOpen}
            options={options}
            placeholder='Selecione uma marca'
            showSearch={{ filterOption: false, onSearch: setSearch }}
            value={value}
        />
    );
}
